// SceneryGeneratorRegistry — Set13 P3: the GENERATOR catalogue, mapping a `generatorID` (a registry
// id on `SceneryKindDef.generatorID`, never a closed union — the Set9 D10 "no recipeKind union"
// decision) to a mesh-build function. This is the bridge between the authored kind and the real 3D
// geometry that the bake consumes (§3.5): the renderer never holds the generators, the WORKER does
// (the `generateSceneryMesh` message (§4.6) is handled by the existing TerrainWorker, which must
// stay DB-free and DOM-free — so this file imports only the pure generators, which are Node-safe).
//
// A new kind is data + one generator function: register it here, give the `SceneryKindDef` a
// `generatorID`, and the bake pipeline (P4) picks it up with no other changes.
import {generateEzTree, GEN_EZTREE_ID} from "./Gen_EzTree.js";
import type {GenParams, SceneryMesh} from "../@SceneryTypes.js";

/** A registered generator: builds the real 3D mesh for one kind from its resolved variant params.
 *  Deterministic (no Math.random), bounded triangle count, ground plane at y=0 (the placer's
 *  `sinkDepth` moves it down), X/Z = ground plane, Y = up. Set14 P3: `variantSeed` (§4.6) is
 *  threaded in — a per-variant seed yields a structurally different tree at zero param-swing cost.
 *  `genParams` is the flat resolved-variant param map; the per-variant seed is optional (the pine
 *  generator ignores it; ez-tree uses it). */
export interface SceneryGenerator {
	/** The `generatorID` this generator answers for. */
	id: string;
	/** Build the mesh for a resolved variant's params + per-variant seed. */
	generate(params: GenParams, variantSeed?: number): SceneryMesh;
}

const registry = new Map<string, SceneryGenerator>();

/** Register a generator (idempotent — later registration for the same id wins; a duplicate id is
 *  a registry bug and is surfaced as a console warning rather than a throw so a hot-reload of a
 *  single generator can't crash the worker). */
export function registerSceneryGenerator(g: SceneryGenerator): void {
	if (registry.has(g.id)) console.warn(`[SceneryGeneratorRegistry] duplicate generator id "${g.id}" — overwriting.`);
	registry.set(g.id, g);
}

/** Look up a generator by id. Returns undefined for an unknown id (the bake pipeline then skips
 *  the kind and reports a warning; the generator set is data + code, and an author can point a
 *  kind at an id whose code isn't loaded — the bake must degrade, never throw). */
export function getSceneryGenerator(id: string): SceneryGenerator | undefined {
	return registry.get(id);
}

/** The built-in generator ids. */
export function builtInSceneryGeneratorIDs(): string[] {
	return [...registry.keys()];
}

// Register the built-ins. Set14 P3 adds the ez-tree generator (the target of this set); the pine
// was deleted in P5 (§4.12 — the stale `"pineTree"` id now hits the unknown-generator degrade path).
registerSceneryGenerator({
	id: GEN_EZTREE_ID,
	generate: (params, variantSeed)=>generateEzTree(params, variantSeed),
});
